<template>
  <form class="w-8 m-auto" @submit.prevent="emit('confirm')">
    <div v-if="erpWellReadings && erpWellReadings.length > 0">
      <h3>Choose Date</h3>
      <InputGroup class="w-fit">
        <Button
          v-for="(readings, i) in erpWellReadings"
          :key="readings?.id || i"
          :class="{
            'p-button-info': _selectedWellReadings.id === readings?.id
          }"
          @click="readings && (_selectedWellReadings = readings)"
        >
          {{ (readings?.reading_date as string).split("-").slice(1).join("/") }}
        </Button>
      </InputGroup>
    </div>
    <h2 class="mb-0 mt-4">Confirm Well Readings Data</h2>
    <p class="body-1 mb-5">
      {{ evalDate }}
    </p>
    <div class="flex mb-3">
      <div>
        <ConverterInput
          v-model="_selectedWellReadings.casing_pressure"
          :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
          :model="PressureUnits.options()"
          :placeholder="
            scenario?.parameters?.bottom_casing_pressure_psia?.toFixed(2)
          "
          label="Casing Pressure"
          mode="write"
          class="mb-0"
        />
        <p class="body-1">
          Suggested Value From Scenario:
          {{ scenario?.parameters?.bottom_casing_pressure_psia?.toFixed(2) }}
          {{ PressureUnits.PoundsPerSquareInchAbsolute.shortLabel }}
        </p>
      </div>
      <div>
        <ConverterInput
          v-model="_selectedWellReadings.tubing_pressure"
          :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
          :model="PressureUnits.options()"
          :placeholder="
            scenario?.parameters?.bottom_tubing_pressure_psia?.toFixed(2)
          "
          label="Tubing Pressure"
          mode="write"
          class="mb-0"
        />
        <p class="body-1">
          Suggested Value From Scenario:
          {{ scenario?.parameters?.bottom_tubing_pressure_psia?.toFixed(2) }}
          {{ PressureUnits.PoundsPerSquareInchAbsolute.shortLabel }}
        </p>
      </div>
    </div>
    <div class="flex mb-3">
      <div>
        <ConverterInput
          v-model="_selectedWellReadings.motor_current"
          :base-unit="CurrentUnits.Amperes"
          :model="CurrentUnits.options()"
          :placeholder="calcs.motorCalculations.motorCurrentA.value.toFixed(2)"
          label="Motor Current"
          mode="write"
          class="mb-0"
        />
        <p class="body-1">
          Suggested Value From Scenario:
          {{ calcs.motorCalculations.motorCurrentA.value.toFixed(2) }}
          {{ CurrentUnits.Amperes.shortLabel }}
        </p>
      </div>
      <div>
        <ConverterInput
          v-model="_selectedWellReadings.motor_frequency"
          :base-unit="FrequencyUnits.Hertz"
          :model="FrequencyUnits.options()"
          :placeholder="scenario?.parameters?.pump_hz?.toFixed(2)"
          label="Motor Frequency"
          mode="write"
          class="mb-0"
        />
        <p class="body-1">
          Suggested Value From Scenario:
          {{ scenario?.parameters?.pump_hz?.toFixed(2) }}
          {{ FrequencyUnits.Hertz.shortLabel }}
        </p>
      </div>
    </div>
    <div class="flex mb-3">
      <div>
        <ConverterInput
          v-model="_selectedWellReadings.pump_intake_pressure"
          :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
          :model="PressureUnits.options()"
          :placeholder="calcs.pumpIntakePressurePSIA.value.toFixed(2)"
          label="Pump Intake Pressure"
          mode="write"
          class="mb-0"
        />
        <p class="body-1">
          Suggested Value From Scenario:
          {{ calcs.pumpIntakePressurePSIA.value.toFixed(2) }}
          {{ PressureUnits.PoundsPerSquareInchAbsolute.shortLabel }}
        </p>
      </div>
      <div>
        <ConverterInput
          v-model="_selectedWellReadings.fluid_temperature"
          :base-unit="TemperatureUnits.Fahrenheit"
          :model="TemperatureUnits.options()"
          :placeholder="calcs.basicData.value.bottomholeTemperatureF.toFixed(2)"
          label="Fluid Temperature"
          mode="write"
          class="mb-0"
        />
        <p class="body-1">
          Suggested Value From Scenario:
          {{ calcs.basicData.value.bottomholeTemperatureF.toFixed(2) }}
          {{ TemperatureUnits.Fahrenheit.shortLabel }}
        </p>
      </div>
    </div>
    <div class="flex mb-3">
      <div>
        <ConverterInput
          v-model="_selectedWellReadings.produced_gas_rate"
          :base-unit="GasFlowRateUnits.ThousandCubicFeetPerDay"
          :model="GasFlowRateUnits.options()"
          :placeholder="
            convert(
              calcs.surfaceFlowRates.value.gasFlowRateSCFD,
              GasFlowRateUnits.CubicFeetPerDay,
              GasFlowRateUnits.ThousandCubicFeetPerDay
            ).toFixed(2)
          "
          label="Produced Gas Rate"
          mode="write"
          class="mb-0"
        />
        <p class="body-1">
          Suggested Value From Scenario:
          {{
            convert(
              calcs.surfaceFlowRates.value.gasFlowRateSCFD,
              GasFlowRateUnits.CubicFeetPerDay,
              GasFlowRateUnits.ThousandCubicFeetPerDay
            ).toFixed(2)
          }}
          {{ GasFlowRateUnits.ThousandCubicFeetPerDay.shortLabel }}
        </p>
      </div>
      <div>
        <ConverterInput
          v-model="_selectedWellReadings.produced_oil_rate"
          :base-unit="FlowRateUnits.BarrelsPerDay"
          :model="FlowRateUnits.options()"
          :placeholder="calcs.surfaceFlowRates.value.oilFlowRateSTBD.toFixed(2)"
          label="Produced Oil Rate"
          mode="write"
          class="mb-0"
        />
        <p class="body-1">
          Suggested Value From Scenario:
          {{ calcs.surfaceFlowRates.value.oilFlowRateSTBD.toFixed(2) }}
          {{ FlowRateUnits.BarrelsPerDay.shortLabel }}
        </p>
      </div>
    </div>
    <div class="flex mb-3">
      <!-- <div>
        <ConverterInput
          v-model="_wellReadings.produced_rate"
          :base-unit="FlowRateUnits.BarrelsPerDay"
          :model="FlowRateUnits.options()"
          label="Produced Rate"
          mode="write"
          class="mb-0"
        />
        <p class="body-1">Suggested Value From Scenario: ¯\_(ツ)_/¯</p>
      </div> -->
      <div>
        <ConverterInput
          v-model="_selectedWellReadings.produced_water_rate"
          :base-unit="FlowRateUnits.BarrelsPerDay"
          :model="FlowRateUnits.options()"
          :placeholder="
            calcs.surfaceFlowRates.value.waterFlowRateSTBD.toFixed(2)
          "
          label="Produced Water Rate"
          mode="write"
          class="mb-0"
        />
        <p class="body-1">
          Suggested Value From Scenario:
          {{ calcs.surfaceFlowRates.value.waterFlowRateSTBD.toFixed(2) }}
          {{ FlowRateUnits.BarrelsPerDay.shortLabel }}
        </p>
      </div>
    </div>

    <div class="flex justify-content-between mr-7">
      <Button type="submit" class="p-button-info"
        >Create Performance Evaluation</Button
      >
      <Button class="p-button-danger" @click="emit('cancel')">Cancel</Button>
    </div>
  </form>
  <br />
</template>

<script setup lang="ts">
import { useVModel } from "@vueuse/core";
import type { ErpWellReadingsQuery, ErpWellReadings } from "gql/api";
import ConverterInput from "../ConverterInput.vue";
import { injectStrict } from "@/utils/utils";
import {
  scenarioCalculationsKey,
  scenarioKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { PressureUnits } from "@/libs/units/pressure";
import { TemperatureUnits } from "@/libs/units/temperature";
import { CurrentUnits } from "@/libs/units/current";
import { FrequencyUnits } from "@/libs/units/frequency";
import { convert } from "@/libs/units";
import { FlowRateUnits, GasFlowRateUnits } from "@/libs/units/flowRate";
import { useToast } from "primevue/usetoast";
import InputGroup from "primevue/inputgroup";
import { computed, watch } from "vue";

const props = defineProps<{
  selectedWellReadings: ErpWellReadings;
  erpWellReadings: ErpWellReadingsQuery["getErpWellReadings"];
}>();
const emit = defineEmits<{
  "update:selectedWellReadings": [val: ErpWellReadings];
  confirm: [];
  cancel: [];
}>();

const calcs = injectStrict(scenarioCalculationsKey);
const scenario = injectStrict(scenarioKey);

const _selectedWellReadings = useVModel(props, "selectedWellReadings", emit);
_selectedWellReadings.value =
  props.erpWellReadings?.toReversed()[0] || _selectedWellReadings.value;

// https://stackoverflow.com/questions/7556591/is-the-javascript-date-object-always-one-day-off
const evalDate = computed(() => {
  const readingDate = _selectedWellReadings.value?.reading_date as string;
  if (!readingDate) return "";
  const dateWithParens = readingDate.replaceAll("-", "/");
  return new Date(dateWithParens).toDateString();
});

const toaster = useToast();

watch(
  () => props.erpWellReadings,
  val => {
    if (Array.isArray(val) && val.length === 0) {
      toaster.add({
        life: 10000,
        severity: "warn",
        summary: "Well Readings Not Found",
        detail: "Spyglass was unable to find any data from this well"
      });
    }
  },
  { immediate: false }
);
</script>

<style scoped lang="scss">
:deep(.converter) {
  margin: 0 1.5rem 1.5rem 0;
}
</style>
